import { useState } from "react";
import { Button, Card, Col, Container, Form, ListGroup, Row } from "react-bootstrap";
import { MdOutlinePrivacyTip, MdOutlineSecurity } from "react-icons/md";
import { IoColorPaletteOutline, IoExtensionPuzzleOutline } from "react-icons/io5";
import { BsReverseLayoutTextSidebarReverse } from "react-icons/bs";
import { LuMousePointer } from "react-icons/lu";
import AppearanceSettings from "./AppearanceSettings";
import SidebarSettings from "./SidebarSettings";
import PrivacySettings from "./PrivacySettings";
import FunctionSettings from "./FunctionSettings";
import { BsShield } from "react-icons/bs";
import ExtensionSettings from "./ExtensionSettings";
import SecuritySettings from "./SecuritySettings";
import UseToggle from "../../../Hooks/UseToggle";
import UpdateProfileName from "../Common/UpdateProfileName";

const AccountSettings = ({
  auth,
  isDark,
  currentUserData,
  plateformSettings,
  setUpdator,
  getSinglePlateformSettings,
}) => {
  const toggles = UseToggle();
  const [activeItem, setActiveItem] = useState("appearance");

  function getTwoInitials(name) {
    if (!name) return "";
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) {
      return parts[0].slice(0, 2).toUpperCase();
    }
    const first = parts[0][0];
    const last = parts[parts.length - 1][0];
    return (first + last).toUpperCase();
  }

  const hasAnyFunctionPermission =
    plateformSettings?.settings?.showFunctionSettings &&
    Object.values(plateformSettings?.settings?.showFunctionSettings).includes(false);

  const hasAnyPrivacyPermission =
    plateformSettings?.settings?.showPrivacySettings &&
    Object.values(plateformSettings?.settings?.showPrivacySettings).includes(false);

  return (
    <>
      <Container fluid>
        <Row>
          <Form.Group as={Col} md={3} className="p-0 border-end">
            <div className="pb-1 pt-3 px-3">
              <h5 className="fw-bold mb-1">Account Settings</h5>
            </div>
            <div
              className="p-3 overflow-auto"
              style={{ height: "calc(100vh - 48px)" }}
            >
              <Card className="border rounded-1 mb-3">
                <Card.Body className="text-center">
                  <div className="hstack justify-content-center mb-2 position-relative">
                    <h6 className={`${isDark ? "bg-secondary" : "bg-primary-subtle"} p-3 rounded-1 fw-bold m-0`}>
                      {getTwoInitials(currentUserData?.name)}
                    </h6>
                    {currentUserData?.twoFAStatus &&
                      <BsShield className="text-primary position-absolute top-50 start-50 translate-middle" style={{ fontSize: 45, opacity: .3 }} />
                    }
                  </div>
                  <h6>{currentUserData?.name}</h6>
                  <Button
                    variant="outline-primary"
                    style={{ fontSize: 13 }}
                    title="Edit Details"
                    className={`rounded-1 py-1 px-3 mt-2 ${activeItem === "editDetails" && "btn-gradient text-white"}`}
                    onClick={() => toggles.setToggle3()}
                  >
                    Edit Details
                  </Button>
                </Card.Body>
              </Card>
              <ListGroup>
                <ListGroup.Item
                  action
                  style={{ fontSize: 13 }}
                  className={`hstack gap-2 ${activeItem === "appearance" &&
                    "btn-gradient text-white rounded-bottom-0"
                    }`}
                  title="Appearance"
                  onClick={() => setActiveItem("appearance")}
                >
                  <IoColorPaletteOutline /> Appearance
                </ListGroup.Item>
                <ListGroup.Item
                  action
                  style={{ fontSize: 13 }}
                  className={`hstack gap-2 ${activeItem === "extensionSettings" && "btn-gradient text-white rounded-0"}`}
                  title="Extension Settings"
                  onClick={() => setActiveItem("extensionSettings")}
                >
                  <IoExtensionPuzzleOutline /> Extension Settings
                </ListGroup.Item>
                <ListGroup.Item
                  action
                  style={{ fontSize: 13 }}
                  className={`hstack gap-2 ${activeItem === "securitySettings" && "btn-gradient text-white rounded-0"}`}
                  title="Security Settings"
                  onClick={() => setActiveItem("securitySettings")}
                >
                  <MdOutlineSecurity /> Security Settings
                </ListGroup.Item>
                {hasAnyPrivacyPermission &&
                  <ListGroup.Item
                    action
                    style={{ fontSize: 13 }}
                    className={`hstack gap-2 ${activeItem === "privacySettings" &&
                      "btn-gradient text-white rounded-0"
                      }`}
                    title="Privacy Settings"
                    onClick={() => setActiveItem("privacySettings")}
                  >
                    <MdOutlinePrivacyTip /> Privacy Settings
                  </ListGroup.Item>
                }
                {hasAnyFunctionPermission &&
                  <ListGroup.Item
                    action
                    style={{ fontSize: 13 }}
                    className={`hstack gap-2 ${activeItem === "functionSettings" &&
                      "btn-gradient text-white rounded-0"
                      }`}
                    title="Function Settings"
                    onClick={() => setActiveItem("functionSettings")}
                  >
                    <LuMousePointer /> Function Settings
                  </ListGroup.Item>
                }

                <ListGroup.Item
                  action
                  style={{ fontSize: 13 }}
                  className={`hstack gap-2 ${activeItem === "sidebarSettings" &&
                    "btn-gradient text-white rounded-top-0"
                    }`}
                  title="Sidebar Settings"
                  onClick={() => setActiveItem("sidebarSettings")}
                >
                  <BsReverseLayoutTextSidebarReverse /> Sidebar Settings
                </ListGroup.Item>
              </ListGroup>
            </div>
          </Form.Group>
          <Form.Group as={Col} md={9} className="p-0">
            {activeItem === "appearance" ? (
              <AppearanceSettings
                getSinglePlateformSettings={getSinglePlateformSettings}
                plateformSettings={plateformSettings}
                auth={auth}
              />
            ) : activeItem === "sidebarSettings" ? (
              <SidebarSettings
                getSinglePlateformSettings={getSinglePlateformSettings}
                plateformSettings={plateformSettings}
                currentUserData={currentUserData}
                auth={auth}
              />
            ) : activeItem === "privacySettings" ? (
              <PrivacySettings
                getSinglePlateformSettings={getSinglePlateformSettings}
                plateformSettings={plateformSettings}
                auth={auth}
              />
            ) : activeItem === "extensionSettings" ? (
              <ExtensionSettings
                getSinglePlateformSettings={getSinglePlateformSettings}
                plateformSettings={plateformSettings}
                auth={auth}
              />
            ) : activeItem === "securitySettings" ? (
              <SecuritySettings setUpdator={setUpdator} currentUserData={currentUserData} auth={auth} />
            ) : (
              activeItem === "functionSettings" && (
                <FunctionSettings
                  currentUserData={currentUserData}
                  getSinglePlateformSettings={getSinglePlateformSettings}
                  plateformSettings={plateformSettings}
                  auth={auth}
                />
              )
            )}
          </Form.Group>
        </Row>
      </Container>
      <UpdateProfileName
        Show={!toggles.toggle3}
        Hide={toggles.setToggle3}
        setUpdator={setUpdator}
        currentUserData={currentUserData}
        Title="Update Name"
        isDark={isDark}
        auth={auth}
      />
    </>
  );
};

export default AccountSettings;
